import classNames from 'clsx';
import interpolateComponents from 'interpolate-components';
import PropTypes from 'prop-types';
import { useContext, useEffect } from 'react';
import {
  CANCEL_FAILED,
  CANCEL_NOTICE,
  CANCEL_SUBSCRIPTION_LINK,
  COMPLETE_CANCELLATION_BUTTON,
  GO_BACK_BUTTON,
  RESTART_COPY,
} from 'src/common/constants/localizations/subscription';
import { LocationAndLocalizationContext } from 'src/common/providers/LocationAndLocalizationProvider';
import asyncStatuses from 'src/common/utils/asyncStatuses';
import useFocus from '../../../../hooks/useFocus';
import InkButton from '../../../shared/button/InkButton';
import CommonDialog from '../../../shared/dialog/CommonDialog';
import subscriptionCss from '../subscription.module.scss';

const CancelConfirmationDialog = ({
  shouldShow,
  handleClose,
  confirmAction,
  chargedThroughDate,
  cancellationStatus,
}) => {
  const [elementRef, setElementRefFocus] = useFocus();

  useEffect(() => {
    if (shouldShow) {
      setElementRefFocus();
    }
  }, [shouldShow, setElementRefFocus]);

  const { getLocalizedText } = useContext(LocationAndLocalizationContext);
  const cancellationPending = cancellationStatus === asyncStatuses.pending;

  // NOTE: https://allyjs.io/data-tables/focusable.html#document-elements helps explains the reasoning for tabIndex='-1'.
  // It's one way to make a natively non-focusable element, focusable.
  return (
    <CommonDialog
      data-testid="cancelConfirmationDialog"
      key="cancelConfirmationDialog"
      dialogOpen={shouldShow}
      handleDialogClose={handleClose}
      maxWidth={560}
      classNames={{
        content: subscriptionCss.dialogSurface,
        body: subscriptionCss.dialogBody,
      }}
    >
      <div data-testid="cancelConfirmationDialogContentWrapper" ref={elementRef} tabIndex="-1">
        <h1 className={subscriptionCss.dialogTitle}>
          {getLocalizedText(CANCEL_SUBSCRIPTION_LINK)}
        </h1>
        <div className={subscriptionCss.dialogSection}>
          <div className={subscriptionCss.dialogContent} data-testid="cancelNotice">
            {interpolateComponents({
              mixedString: getLocalizedText(CANCEL_NOTICE),
              components: {
                date: <span>{chargedThroughDate}</span>,
              },
            })}
          </div>
        </div>
        <div className={subscriptionCss.dialogSection}>
          <div className={subscriptionCss.dialogContent} data-testid="restartCopy">
            {getLocalizedText(RESTART_COPY)}
          </div>
        </div>
        {cancellationStatus === asyncStatuses.rejected && (
          <div
            data-testid="cancelFailed"
            className={classNames(subscriptionCss.dialogSection, subscriptionCss['error-message'])}
          >
            {getLocalizedText(CANCEL_FAILED)}
          </div>
        )}
        <div className={subscriptionCss.dialogButtonWrapper}>
          <InkButton
            id="cancelCompleteButton"
            dataTestId="cancelCompleteButton"
            label={getLocalizedText(COMPLETE_CANCELLATION_BUTTON)}
            onClick={confirmAction}
            isDisabled={cancellationPending}
          />

          <InkButton
            id="cancelGoBackButton"
            dataTestId="cancelGoBackButton"
            label={getLocalizedText(GO_BACK_BUTTON)}
            onClick={handleClose}
            isDisabled={cancellationPending}
            className={subscriptionCss.cancelButton}
          />
        </div>
      </div>
    </CommonDialog>
  );
};

CancelConfirmationDialog.propTypes = {
  shouldShow: PropTypes.bool.isRequired,
  cancellationStatus: PropTypes.string,
  handleClose: PropTypes.func.isRequired,
  confirmAction: PropTypes.func.isRequired,
  chargedThroughDate: PropTypes.string.isRequired,
};

export default CancelConfirmationDialog;
